import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Settings } from 'lucide-react';
import { safeLocalStorageSet } from '../utils/storageValidator';

/**
 * CollapsibleSection Component
 *
 * PURPOSE: Reusable collapsible section with optional settings menu
 * WHY: Reduces UI clutter, improves navigation, provides consistent UX
 *
 * FEATURES:
 * - Smooth expand/collapse animation with CSS transitions
 * - Optional settings dropdown menu with custom actions
 * - localStorage persistence (remembers collapsed state)
 * - Optional badge/count display in header
 * - Click-anywhere-on-header to toggle
 * - Mobile-friendly touch targets (48px min)
 * - Accessible with ARIA labels and keyboard navigation
 * - Custom icons and colors
 *
 * USAGE:
 * <CollapsibleSection
 *   title="Weapon Proficiencies"
 *   icon={<Sword size={16} />}
 *   storageKey="basics-weapon-profs"
 *   defaultCollapsed={false}
 *   badge={5}
 *   settingsMenu={[
 *     { label: 'Add Proficiency', icon: <Plus />, onClick: () => {...} },
 *     { label: 'Reset All', icon: <Trash2 />, onClick: () => {...}, danger: true }
 *   ]}
 * >
 *   <div>Section content here</div>
 * </CollapsibleSection>
 *
 * Created: October 10, 2025
 */

const CollapsibleSection = ({
  // Required props
  title,
  children,

  // Optional props
  icon = null, // Icon component (e.g., <Shield size={16} />)
  storageKey = null, // localStorage key for persistence (e.g., "basics-armor-profs")
  defaultCollapsed = false, // Initial state if no saved state exists
  badge = null, // Number or string to display in header (e.g., count of items)
  badgeColor = 'bg-blue-600', // Tailwind class for badge color
  settingsMenu = null, // Array of menu items: [{ label, icon, onClick, danger }]
  headerClassName = '', // Additional classes for header
  contentClassName = '', // Additional classes for content wrapper
  onToggle = null, // Callback when section is toggled: (isCollapsed) => void
  disabled = false, // Disable toggling
  alwaysExpanded = false, // Force section to stay expanded
  testId = null // data-testid for testing
}) => {
  // [STATE]: Manage collapsed state with localStorage persistence
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (alwaysExpanded) return false;
    if (!storageKey) return defaultCollapsed;

    try {
      const saved = localStorage.getItem(storageKey);
      return saved !== null ? JSON.parse(saved) : defaultCollapsed;
    } catch {
      return defaultCollapsed;
    }
  });

  // [STATE]: Settings dropdown open/close
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef(null);

  // [EFFECT]: Save collapsed state to localStorage when changed
  useEffect(() => {
    if (storageKey && !alwaysExpanded) {
      try {
        safeLocalStorageSet(storageKey, JSON.stringify(isCollapsed));
      } catch (error) {
        console.error('Failed to save collapsed state:', error);
      }
    }
  }, [isCollapsed, storageKey, alwaysExpanded]);

  // [EFFECT]: Close settings dropdown when clicking outside
  useEffect(() => {
    if (!showSettings) return;

    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  // [HANDLER]: Toggle collapsed state
  const handleToggle = () => {
    if (disabled || alwaysExpanded) return;

    const newState = !isCollapsed;
    setIsCollapsed(newState);

    if (onToggle) {
      onToggle(newState);
    }
  };

  // [HANDLER]: Settings menu item click
  const handleSettingsClick = (e, item) => {
    e.stopPropagation(); // Don't trigger section toggle
    setShowSettings(false);
    if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <div
      className="collapsible-section"
      data-testid={testId}
      data-collapsed={isCollapsed}
    >
      {/* Section Header - Clickable to toggle */}
      <div
        className={`
          flex items-center justify-between gap-2 py-2 px-3 rounded-lg
          ${disabled || alwaysExpanded ? 'cursor-default' : 'cursor-pointer hover:bg-gray-700/50'}
          transition-colors duration-150
          ${headerClassName}
        `}
        onClick={handleToggle}
        role="button"
        tabIndex={disabled || alwaysExpanded ? -1 : 0}
        aria-expanded={!isCollapsed}
        aria-label={`${title} section, ${isCollapsed ? 'collapsed' : 'expanded'}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        {/* Left side: Icon + Title + Badge */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {/* Chevron icon for expand/collapse */}
          {!alwaysExpanded && (
            <div className="flex-shrink-0 text-gray-400">
              {isCollapsed ? (
                <ChevronRight size={18} className="transition-transform duration-200" />
              ) : (
                <ChevronDown size={18} className="transition-transform duration-200" />
              )}
            </div>
          )}

          {/* Optional icon */}
          {icon && (
            <div className="flex-shrink-0 text-gray-400">
              {icon}
            </div>
          )}

          {/* Title */}
          <h3 className="text-sm font-semibold text-gray-300 truncate">
            {title}
          </h3>

          {/* Optional badge */}
          {badge !== null && badge !== undefined && (
            <span className={`
              ${badgeColor} text-white text-xs font-medium px-2 py-0.5 rounded-full
              flex-shrink-0
            `}>
              {badge}
            </span>
          )}
        </div>

        {/* Right side: Settings menu */}
        {settingsMenu && settingsMenu.length > 0 && (
          <div className="relative flex-shrink-0" ref={settingsRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowSettings(!showSettings);
              }}
              className="p-2 hover:bg-gray-600 rounded transition-colors"
              aria-label="Section settings"
              title="Settings"
            >
              <Settings size={16} className="text-gray-400" />
            </button>

            {/* Settings Dropdown Menu */}
            {showSettings && (
              <div className="
                absolute right-0 top-full mt-1 z-50
                bg-gray-800 border border-gray-700 rounded-lg shadow-xl
                min-w-[200px] py-1
                animate-fade-in
              ">
                {settingsMenu.map((item, index) => (
                  <button
                    key={index}
                    onClick={(e) => handleSettingsClick(e, item)}
                    disabled={item.disabled}
                    className={`
                      w-full px-4 py-2 text-left text-sm
                      flex items-center gap-2
                      transition-colors
                      ${item.disabled
                        ? 'text-gray-500 cursor-not-allowed'
                        : item.danger
                          ? 'text-red-400 hover:bg-red-900/30'
                          : 'text-gray-300 hover:bg-gray-700'
                      }
                    `}
                  >
                    {item.icon && (
                      <span className="flex-shrink-0">
                        {item.icon}
                      </span>
                    )}
                    <span className="flex-1">{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section Content - Animated expand/collapse */}
      <div
        className={`
          overflow-hidden transition-all duration-300 ease-in-out
          ${isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[5000px] opacity-100'}
        `}
      >
        <div className={`pt-2 ${contentClassName}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
